import { ActionIcon, AppShell, Avatar, Badge, Button, Divider, Flex, Group, Image, Indicator, Menu, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconBell, IconBuildingHospital, IconChevronDown, IconCookie, IconDownload, IconFileAnalytics, IconLanguage, IconLock, IconLogout, IconMenu2, IconSettings2 } from '@tabler/icons-react';
import i18n, { t } from 'i18next';
import { inject, observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePatientAlerts, useAlerts } from '../../entities/Alert/queries';
import { IClinicalAlert, AlertSeverity } from '../../entities/Alert/Alert';
import { useGetUserPreferences } from '../../entities/UserConsent/queries/useGetUserPreferences.tsx';
import Utils from '../../helpers/utils';
import appmedLogo from '../../../assets/icon-192x192.png';
import { patientStore } from '../../../global/Store/Store.ts';
import { usePostLogout } from '../../entities/Login/mutations/usePostLogoutMutations.tsx';
import { usePostSetLanguageMutation } from '../../entities/Login/mutations/usePostSetLanguageMutation.tsx';
import { useSessionProvider } from '../../provider/sessionContext.tsx';
import { useNavProvider } from '../../provider/navigationContext.tsx';
import { theme } from '../../style/theme.tsx';
import CookiePreferencesModal from '../organism/CookiePreferencesModal/CookiePreferencesModal.tsx';
import { isPWAInstallable, resetPWAPrompt } from '../PWAInstallPrompt/pwaUtils.ts';
import AuditLogDrawer from '../organism/AuditLogDrawer/AuditLogDrawer.tsx';
import { useGetPatientsProfile } from '../../entities/Patients/queries/useGetPatientsProfiles.tsx';

const severityColorMap: Record<string, string> = {
    Low: 'blue',
    Moderate: 'yellow',
    High: 'orange',
};

interface AlertBellMenuProps {
    alerts: IClinicalAlert[] | undefined;
    alertRoute: string;
    linkToDetail?: boolean;
    isPatient?: boolean;
}

const AlertBellMenu: React.FC<AlertBellMenuProps> = ({ alerts, alertRoute, linkToDetail, isPatient }) => {
    const navigate = useNavigate();

    const openAlerts = alerts?.filter((a) => !a.isVisited) ?? [];
    const openCount = openAlerts.length;

    const getLocalizedTitle = (alert: IClinicalAlert): string => {
        if (isPatient) {
            const key = `alert.patient.description.${alert.sourceType}`;
            const translated = t(key);
            return translated === key ? t('alert.patient.description.default') : translated;
        }
        return i18n.language === 'fr' ? alert.titleFr : alert.titleEn;
    };

    return (
        <Menu shadow="md" width={300} position="bottom-end">
            <Menu.Target>
                <Indicator
                    color="red"
                    size={18}
                    label={openCount > 0 ? openCount : undefined}
                    disabled={openCount === 0}
                    offset={4}
                >
                    <ActionIcon variant="subtle" size="lg" color="gray">
                        <IconBell size={22} />
                    </ActionIcon>
                </Indicator>
            </Menu.Target>
            <Menu.Dropdown style={{ maxWidth: 300, overflow: 'hidden' }}>
                <Menu.Label>{t('alert.title')}</Menu.Label>
                {openAlerts.length === 0 ? (
                    <Menu.Item disabled>
                        <Text size="sm" c="dimmed">
                            {t('alert.noAlerts')}
                        </Text>
                    </Menu.Item>
                ) : (
                    openAlerts.slice(0, 5).map((alert) => (
                        <Menu.Item
                            key={alert.id}
                            onClick={() => navigate(linkToDetail ? `${alertRoute}/${alert.id}` : alertRoute)}
                            style={{ overflow: 'hidden' }}
                        >
                            <Group gap="xs" wrap="nowrap" style={{ overflow: 'hidden' }}>
                                {!isPatient && (
                                    <Badge
                                        color={severityColorMap[alert.severity] || 'gray'}
                                        variant="filled"
                                        size="xs"
                                        circle
                                        style={{ flexShrink: 0 }}
                                    >
                                        {' '}
                                    </Badge>
                                )}
                                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                    {!isPatient && alert.patientName && (
                                        <Text size="xs" fw={600}>
                                            {alert.patientName}
                                        </Text>
                                    )}
                                    <Text size="sm" fw={500} style={{ wordBreak: 'break-word' }}>
                                        {getLocalizedTitle(alert)}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                        {Utils.formatLocalDateToYYYYMMDD(alert.triggeredDate)}{' '}
                                        {Utils.formatLocalDateToTime(alert.triggeredDate)}
                                    </Text>
                                </Stack>
                            </Group>
                        </Menu.Item>
                    ))
                )}
                {openAlerts.length > 5 && (
                    <>
                        <Divider />
                        <Menu.Item onClick={() => navigate(alertRoute)}>
                            <Text size="sm" ta="center" c="blue">
                                {t('alert.viewAll')}
                            </Text>
                        </Menu.Item>
                    </>
                )}
            </Menu.Dropdown>
        </Menu>
    );
};

const PatientAlertBell: React.FC = () => {
    const { data: alerts } = usePatientAlerts();
    return <AlertBellMenu alerts={alerts} alertRoute="/patientDashboard/patientAlerts" isPatient />;
};

const severityOrder: Record<string, number> = {
    [AlertSeverity.Low]: 0,
    [AlertSeverity.Moderate]: 1,
    [AlertSeverity.High]: 2,
};

const HcpAlertBell: React.FC = () => {
    const { currentUser } = useSessionProvider();
    const { data: alerts } = useAlerts({
        facilityId: currentUser?.selectedHealthcareFacilityID ?? undefined,
    });
    const { data: preferences } = useGetUserPreferences();
    const minSeverity = preferences?.alertBellMinSeverity ?? 'Low';
    const minOrder = severityOrder[minSeverity] ?? 0;

    const filteredAlerts = alerts?.filter(
        (alert) => (severityOrder[alert.severity] ?? 0) >= minOrder,
    );

    return <AlertBellMenu alerts={filteredAlerts} alertRoute="/hcpDashboard/alert" linkToDetail />;
};

const Header: React.FC = inject()(
    observer(() => {
        const queryClient = useQueryClient();
        const useLogoutMutation = usePostLogout();
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [cookiePreferencesOpened, setCookiePreferencesOpened] = useState(false);
        const [auditLogDrawerOpened, setAuditLogDrawerOpened] = useState(false);
        const useSetLanguageMutation = usePostSetLanguageMutation();
        const { currentUser, setCurrentUser } = useSessionProvider();
        const isAnonymizedMode = patientStore.isAnonymizedMode;
        const { data: patientProfile } = useGetPatientsProfile();
        const urlParams = new URLSearchParams(window.location.search);
        const language = urlParams.get('language');
        
        // Media query to detect small screens (phone portrait)
        const isSmallScreen = useMediaQuery('(max-width: 768px)');
        const isPortrait = useMediaQuery('(orientation: portrait)');
        const isTouchDevice = useMediaQuery('(pointer: coarse)');
        const isMobile = isSmallScreen && isPortrait && isTouchDevice;
        const { open: openNav } = useNavProvider();

        const navigate = useNavigate();

        const onClickProfileSetting = async () => {
            navigate(
                currentUser?.patientID
                    ? '/patientDashboard/profile'
                    : `/hcpDashboard/hcpProfile/${currentUser?.healthcareProfessionalID}`,
            );
        };

        useEffect(() => {
            if (language && i18n.language !== language) 
                onClickSetLanguage();
            
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [language]);

        const onClickSetLanguage = async () => {
            setIsSubmitting(true);
            try {
                const newLanguage = i18n.language === 'fr' ? 'en' : 'fr';

                await useSetLanguageMutation.mutateAsync({
                    language: newLanguage,
                });

                await i18n.changeLanguage(newLanguage);
                localStorage.setItem('i18nextLng', newLanguage);
            } catch (e) {
                console.error(e);
            } finally {
                setIsSubmitting(false);
            }
        };

        const onClickSecurity = async () => {
            navigate('changePassword');
        };

        const onClickLogout = async () => {
            await useLogoutMutation.mutateAsync();
        };

        const onClickInstallApp = () => {
            resetPWAPrompt();
        };

        const showInstallAppOption = isPWAInstallable();

        const facilities = currentUser?.healthcareFacilities ?? [];
        const hasMultipleFacilities = facilities.length > 1;
        const currentFacilityName = facilities.find(
            f => f.id === currentUser?.selectedHealthcareFacilityID
        )?.name;

        const handleClinicChange = (facilityId: number) => {
            if (currentUser) {
                const updatedUser = { ...currentUser, selectedHealthcareFacilityID: facilityId };
                setCurrentUser(updatedUser as any);
                localStorage.setItem('currentUser', JSON.stringify(updatedUser));
                queryClient.invalidateQueries();

                const path = window.location.pathname;
                if (path.startsWith('/hcpPatientDashboard')) 
                    navigate('/hcpDashboard');
                 else if (/^\/hcpDashboard\/task\/\d+/.test(path)) 
                    navigate('/hcpDashboard/task');
                 else if (/^\/hcpDashboard\/alert\/\d+/.test(path)) 
                    navigate('/hcpDashboard/alert');
                
            }
        };

        return (
            <AppShell.Header 
                style={{ 
                    shadow: theme.shadows?.md,
                    paddingTop: 'var(--safe-area-inset-top, 0px)',
                    paddingLeft: 'var(--safe-area-inset-left, 0px)',
                    paddingRight: 'var(--safe-area-inset-right, 0px)',
                    height: 'calc(60px + var(--safe-area-inset-top, 0px))',
                    minHeight: 'calc(60px + var(--safe-area-inset-top, 0px))',
                }}
            >
                <Flex 
                    align={'center'} 
                    justify={'space-between'} 
                    gap={'sm'} 
                    w={'100%'} 
                    ml={'md'} 
                    mr={'md'}
                    h={60}
                >
                    <Flex align="center" gap="xs">
                        {isMobile && currentUser && (
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="lg"
                                onClick={openNav}
                                aria-label="Open menu"
                            >
                                <IconMenu2 size={24} />
                            </ActionIcon>
                        )}
                        <Image
                            style={{ cursor: 'pointer' }}
                            w={'auto'}
                            h={40}
                            ml={isMobile ? 0 : 'md'}
                            src={appmedLogo}
                        />
                    </Flex>
                    <Flex align={'center'} ml={'auto'} mr={'xs'} gap={'sm'}>
                        {currentUser?.healthcareProfessionalID && currentFacilityName && (
                            hasMultipleFacilities ? (
                                <Menu shadow="md" width={240} position="bottom-end">
                                    <Menu.Target>
                                        <Badge
                                            variant="dot"
                                            color="teal"
                                            size="md"
                                            style={{ cursor: 'pointer', maxWidth: '100%', whiteSpace: 'nowrap' }}
                                            rightSection={<IconChevronDown size={12} />}
                                        >
                                            {currentFacilityName}
                                        </Badge>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <Menu.Label>{t('header.currentClinic')}</Menu.Label>
                                        {facilities.map((facility) => (
                                            <Menu.Item
                                                key={facility.id}
                                                leftSection={<IconBuildingHospital size={16} />}
                                                onClick={() => handleClinicChange(facility.id)}
                                                fw={facility.id === currentUser?.selectedHealthcareFacilityID ? 700 : 400}
                                                c={facility.id === currentUser?.selectedHealthcareFacilityID ? 'teal' : undefined}
                                            >
                                                {facility.name}
                                            </Menu.Item>
                                        ))}
                                    </Menu.Dropdown>
                                </Menu>
                            ) : (
                                <Badge
                                    variant="dot"
                                    color="teal"
                                    size="md"
                                    style={{ cursor: 'default', maxWidth: '100%', whiteSpace: 'nowrap' }}
                                >
                                    {currentFacilityName}
                                </Badge>
                            )
                        )}
                        {currentUser?.patientID && patientProfile?.therapeuticIndicationName && (
                            <Badge
                                variant="dot"
                                color="teal"
                                size="lg"
                                style={{ cursor: 'default', maxWidth: '100%', whiteSpace: 'nowrap' }}
                            >
                                {patientProfile.therapeuticIndicationName}
                            </Badge>
                        )}
                        {currentUser?.patientID && <PatientAlertBell />}
                        {currentUser?.healthcareProfessionalID && <HcpAlertBell />}
                        {!(isSmallScreen && currentUser?.hasProfile) && (
                            <Tooltip label={t('navbar.changeLanguage')} position="bottom" transitionProps={{ duration: 0 }}>
                                <Button
                                    mr={currentUser ? 'sm' : 'lg'}
                                    ml={currentUser ? 'sm' : 'lg'}
                                    p={'xs'}
                                    variant={i18n.language === 'fr' ? 'filled' : 'default'}
                                    color={i18n.language === 'fr' ? 'blue' : 'gray'}
                                    loading={isSubmitting}
                                    onClick={() => onClickSetLanguage()}
                                    size={'sm'}
                                >
                                    {i18n.language === 'fr' ? 'FR' : 'EN'}
                                </Button>
                            </Tooltip>
                        )}
                        {currentUser?.hasProfile && (
                            <Menu 
                                shadow="md" 
                                width={isSmallScreen ? 250 : 200}
                                position={isSmallScreen ? 'bottom-end' : 'bottom'}
                                offset={isSmallScreen ? 5 : 0}
                                zIndex={isSmallScreen ? 1000 : undefined}
                            >
                                <Menu withinPortal={isSmallScreen} width={isSmallScreen ? 250 : 'target'}>
                                    <Menu.Target>
                                        <Button
                                            size="lg"
                                            p={0}
                                            mr={isSmallScreen ? 'md' : 'xl'}
                                            styles={{ label: { color: 'black' } }}
                                            leftSection={
                                                <Avatar
                                                    color="blue"
                                                    size="md"
                                                    variant="filled"
                                                    title={currentUser.displayName}
                                                />
                                            }
                                            rightSection={
                                                !isSmallScreen ? (
                                                    <ThemeIcon size="xs" variant="transparent" color="dark">
                                                        <IconChevronDown />
                                                    </ThemeIcon>
                                                ) : null
                                            }
                                            variant="transparent"
                                        >
                                            {!isSmallScreen ? currentUser.displayName : ''}
                                        </Button>
                                    </Menu.Target>

                                    <Menu.Dropdown style={{ 
                                        width: '100%', 
                                        minWidth: isSmallScreen ? '250px' : '200px',
                                        zIndex: isSmallScreen ? 1001 : undefined
                                    }}>
                                        <Stack gap={'xs'}>
                                            {isSmallScreen && (
                                                <>
                                                    <Menu.Item
                                                        styles={{ 
                                                            item: { 
                                                                alignItems: 'center',
                                                                fontWeight: 600,
                                                                color: 'var(--mantine-color-blue-7)',
                                                                cursor: 'default',
                                                                padding: '12px 16px',
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-word',
                                                                textAlign: 'center',
                                                                fontSize: '18px'
                                                            } 
                                                        }}
                                                        onClick={(e) => e.preventDefault()}
                                                    >
                                                        {currentUser.displayName}
                                                    </Menu.Item>
                                                    <Divider />
                                                </>
                                            )}
                                            {((currentUser?.patientID && !isAnonymizedMode) || currentUser?.healthcareProfessionalID) && (
                                                <Menu.Item
                                                    styles={{ item: { alignItems: 'self-start' } }}
                                                    leftSection={<IconSettings2 size={20} />}
                                                    onClick={onClickProfileSetting}
                                                >
                                                    {t('header.profileSettings')}
                                                </Menu.Item>
                                            )}
                                            {isSmallScreen && (
                                                <Menu.Item
                                                    styles={{ item: { alignItems: 'self-start' } }}
                                                    leftSection={<IconLanguage size={20} />}
                                                    onClick={() => onClickSetLanguage()}
                                                >
                                                    {t('navbar.changeLanguage')} ({i18n.language === 'fr' ? 'EN' : 'FR'})
                                                </Menu.Item>
                                            )}
                                            <Menu.Item
                                                styles={{ item: { alignItems: 'self-start' } }}
                                                leftSection={<IconLock size={20} />}
                                                onClick={onClickSecurity}
                                            >
                                                {t('formChangePassword.changePassword')}
                                            </Menu.Item>
                                            {showInstallAppOption && (
                                                <Menu.Item
                                                    styles={{ item: { alignItems: 'self-start' } }}
                                                    leftSection={<IconDownload size={20} />}
                                                    onClick={onClickInstallApp}
                                                >
                                                    {t('header.installApp')}
                                                </Menu.Item>
                                            )}
                                            {currentUser?.showAuditLogViewer && (
                                                <Menu.Item
                                                    styles={{ item: { alignItems: 'self-start' } }}
                                                    leftSection={<IconFileAnalytics size={20} />}
                                                    onClick={() => setAuditLogDrawerOpened(true)}
                                                >
                                                    {t('auditLog.realtime.menuItem')}
                                                </Menu.Item>
                                            )}
                                            <Divider />
                                            <Menu.Item
                                                styles={{ item: { alignItems: 'self-start' } }}
                                                leftSection={<IconCookie size={20} />}
                                                onClick={() => setCookiePreferencesOpened(true)}
                                            >
                                                {t('navbar.cookiePreferences')}
                                            </Menu.Item>
                                            <Divider />
                                            <Menu.Item
                                                styles={{ item: { alignItems: 'self-start' } }}
                                                leftSection={<IconLogout size={20} />}
                                                onClick={onClickLogout}
                                            >
                                                {t('header.logout')}
                                            </Menu.Item>
                                        </Stack>
                                    </Menu.Dropdown>
                                </Menu>
                            </Menu>
                        )}
                    </Flex>
                </Flex>
                <CookiePreferencesModal
                    opened={cookiePreferencesOpened}
                    onClose={() => setCookiePreferencesOpened(false)}
                    onSave={() => setCookiePreferencesOpened(false)}
                />
                {currentUser?.showAuditLogViewer && (
                    <AuditLogDrawer
                        opened={auditLogDrawerOpened}
                        onClose={() => setAuditLogDrawerOpened(false)}
                    />
                )}
            </AppShell.Header>
        );
    }),
);

export default Header;
